/**
 * TripDetailsDrawer — Detailansicht einer Fahrt in einem `Drawer`.
 *
 * Mobil von unten (`anchor="bottom"`), ab `md` von rechts (`anchor="right"`,
 * 440 px). Inhalt: Titel, Transportmittel, Startzeit/Treffpunkt. Status, Kinder
 * und Mitfahrer werden auf der Tageskarte gezeigt. Alle Schreibzugriffe laufen
 * über `onAction`.
 *
 * `prefers-reduced-motion` wird respektiert: bei reduzierter Bewegung wird die
 * Slide-Transition deaktiviert.
 */

import { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useI18n } from "../../i18n/I18nProvider";
import type { TransportMode, TripAction, TripView } from "./types";

const MODES: TransportMode[] = ["bike", "car", "bus"];

export default function TripDetailsDrawer({
  open,
  trip,
  dayLabel,
  disabled,
  onAction,
  onClose,
}: {
  open: boolean;
  /** Die aktuell im Drawer gezeigte Fahrt (oder null, wenn geschlossen). */
  trip: TripView | null;
  dayLabel: string;
  disabled: boolean;
  onAction: (action: TripAction) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Lokaler Entwurf des Treffpunkts; wird bei Blur/Enter übernommen.
  const [meetingDraft, setMeetingDraft] = useState(trip?.meetingPoint ?? "");
  useEffect(() => {
    setMeetingDraft(trip?.meetingPoint ?? "");
  }, [trip?.meetingPoint, trip?.id]);

  const directionLabel =
    trip?.direction === "afternoon"
      ? t("weeklyPlan.direction.afternoon")
      : t("weeklyPlan.direction.morning");

  return (
    <Drawer
      anchor={wide ? "right" : "bottom"}
      open={open}
      onClose={onClose}
      transitionDuration={reduceMotion ? 0 : undefined}
      PaperProps={{
        sx: wide
          ? {
              width: 440,
              maxWidth: "100%",
              borderRadius: "22px 0 0 22px",
              backgroundImage: "none",
            }
          : {
              maxWidth: 430,
              mx: "auto",
              borderRadius: "22px 22px 0 0",
              maxHeight: "92vh",
              pb: "env(safe-area-inset-bottom, 0px)",
              backgroundImage: "none",
            },
      }}
    >
      {trip != null && (
        <Box p={2} pb={3}>
          <Typography variant="h6" fontWeight={700}>
            {dayLabel} · {directionLabel}
          </Typography>

          <Typography variant="subtitle2" mt={2} mb={1}>
            {t("weeklyPlan.transport.aria", { direction: directionLabel })}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="primary"
            value={trip.transportMode}
            disabled={disabled}
            aria-label={t("weeklyPlan.transport.aria", { direction: directionLabel })}
            onChange={(_e, v: TransportMode | null) => {
              if (v) onAction({ kind: "mode", tripId: trip.id, transportMode: v });
            }}
          >
            {MODES.map((m) => (
              <ToggleButton key={m} value={m} sx={{ minHeight: 48 }}>
                {t(`weeklyPlan.transport.${m}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" gap={1.5} mt={2}>
            <TextField
              label={t("weeklyPlan.trip.startTimeAria", { direction: directionLabel })}
              type="time"
              value={trip.startTime ?? ""}
              disabled={disabled}
              onChange={(e) =>
                onAction({
                  kind: "details",
                  tripId: trip.id,
                  startTime: e.target.value === "" ? null : e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label={t("weeklyPlan.trip.meetingPoint")}
              value={meetingDraft}
              disabled={disabled}
              inputProps={{ maxLength: 255 }}
              onChange={(e) => setMeetingDraft(e.target.value)}
              onBlur={() => {
                const next = meetingDraft.trim();
                if (next !== (trip.meetingPoint ?? "")) {
                  onAction({
                    kind: "details",
                    tripId: trip.id,
                    meetingPoint: next === "" ? null : next,
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              fullWidth
            />
          </Stack>

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, minHeight: 52 }}
            onClick={onClose}
          >
            {t("weeklyPlan.comments.done")}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
